import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Combobox } from '../../components/ui/Combobox';
import { listContacts } from '../contacts';
import { computeDisplayName } from '../../types/contact';
import type { Account } from '../../types/account';
import {
  PORTAL_PERMISSIONS,
  PORTAL_PERMISSION_BUILT,
  PORTAL_PERMISSION_LABELS,
  type PortalPermission,
} from '../../types/portalUser';
import { invitePortalUser, type InvitePortalUserResult } from './api';

// CLIENT PORTAL — the invite dialog, on the account page.
//
// ⚠️ THE PERSON IS PICKED FROM THE ACCOUNT'S EXISTING CONTACTS, not typed in as
// a free-text address (Scott's call: one source of truth for who a client's
// people are). That also removes the most likely way to send a sign-in link to
// the wrong inbox — a typo — because the address comes from a record someone
// already checked.
//
// ⚠️⚠️ THERE IS NO ACCOUNT PICKER, AND THERE MUST NOT BE. Since 2026-09-02 the
// scope is the CONTACT'S account links, so this dialog grants a person the
// ability to sign in — not a list of accounts. `fromAccountId` is the account
// whose page this was opened from: it is checked for coverage and named in the
// answer, and grants nothing. An account picker here would be a second
// authority, which is the drift that change removed.
//
// ⚠️ RE-SHARING AN ALREADY-INVITED CONTACT IS A SUCCESS, not an error. It used
// to answer "that email already has portal access" — true, and useless, since
// their access may not have covered the location the AM was standing on. The
// server now checks coverage FIRST, so a repeat share is a genuine no-op and
// says what they have.

type Props = {
  account: Account;
  onClose: () => void;
  onDone: () => void;
};

/** ⚠️ Locations, not links: what the client will actually be able to open. */
function coverageLabel(locationCount: number): string {
  if (locationCount === 0) return 'no locations yet';
  return locationCount === 1 ? '1 location' : `${locationCount} locations`;
}

export function InvitePortalUserModal({ account, onClose, onDone }: Props) {
  const [contactId, setContactId] = useState('');
  const [permissions, setPermissions] = useState<PortalPermission[]>(['reporting']);
  const [result, setResult] = useState<InvitePortalUserResult | null>(null);

  const contactsQuery = useQuery({
    queryKey: ['contacts', account.id],
    queryFn: () => listContacts(account.id),
  });

  const contacts = contactsQuery.data ?? [];
  const chosen = contacts.find((c) => c.id === contactId) ?? null;
  const chosenEmail = chosen?.email?.trim() ?? '';

  const mut = useMutation({
    mutationFn: () =>
      invitePortalUser({
        email: chosenEmail,
        displayName: chosen ? computeDisplayName(chosen) : '',
        contactId,
        fromAccountId: account.id,
        permissions,
      }),
    onSuccess: (res) => {
      setResult(res);
      onDone();
    },
  });

  const togglePermission = (p: PortalPermission) => {
    setPermissions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const grantsDescendants = account.accountType !== 'location' && account.accountType !== 'dealership';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Give portal access</h2>
        <p className="text-sm text-slate-600 mb-4">
          They'll get an email with a sign-in link. There's no password.
        </p>

        {result ? (
          <div className="space-y-3 text-sm">
            {/* ⚠️ TWO OUTCOMES, BOTH SUCCESSES, AND THE WORDING DIFFERS BECAUSE
                THE FACTS DO. "Now has access" for a fresh invite; for one that
                was already in place, what they actually have — a count of the
                locations their contact links resolve to, and the confirmation
                that this account is among them. Saying "now has access" for a
                no-op would be untrue, and saying "already has access" without
                the coverage is the useless answer this replaced. */}
            <p className="text-emerald-700">
              {result.created
                ? `${result.portalUser.displayName} now has access to ${account.name}.`
                : `${result.portalUser.displayName} already has access — covering ${coverageLabel(
                    result.portalUser.locationCount,
                  )}, including this one.`}
            </p>
            {!result.created && (
              <p className="text-slate-600">
                Nothing to do. They can sign in already; use{' '}
                <span className="font-medium">Resend link</span> in the list below if they need
                a fresh sign-in link.
              </p>
            )}
            {result.created && result.emailSent ? (
              <p className="text-slate-600">
                A sign-in link is on its way to {result.portalUser.email}.
              </p>
            ) : result.created ? (
              // ⚠️ Stated plainly rather than glossed. The grant is real, so
              // rolling it back would be wrong — but if the AM walks away
              // believing the email landed, the client is left waiting for
              // something that never arrives.
              <p className="text-amber-700">
                Access is set up, but the email didn't send
                {result.emailError ? `: ${result.emailError}` : '.'} Use{' '}
                <span className="font-medium">Resend</span> in the list below.
              </p>
            ) : null}
            <div className="pt-2 flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </div>
        ) : (
          <>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contact</label>
            <Combobox
              aria-label="Contact"
              placeholder={contactsQuery.isLoading ? 'Loading…' : 'Select a contact…'}
              searchPlaceholder="Search contacts…"
              value={contactId}
              onValueChange={setContactId}
              disabled={mut.isPending || contactsQuery.isLoading}
              options={contacts.map((c) => ({
                value: c.id,
                label: computeDisplayName(c),
                description: c.email ?? 'No email address',
                keywords: c.email ?? '',
                // A contact with no email cannot be sent a sign-in link, and
                // saying so at the point of choosing beats a validation error.
                disabled: !c.email?.trim(),
              }))}
            />
            {contacts.length === 0 && !contactsQuery.isLoading && (
              <p className="mt-2 text-sm text-slate-600">
                This account has no contacts yet.{' '}
                <Link to="/contacts/new" className="text-kyck-700 underline">
                  Add one first
                </Link>
                .
              </p>
            )}

            <fieldset className="mt-4">
              <legend className="block text-sm font-medium text-slate-700 mb-1">
                What they can see
              </legend>
              <div className="space-y-1.5">
                {PORTAL_PERMISSIONS.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={permissions.includes(p)}
                      onChange={() => togglePermission(p)}
                      disabled={mut.isPending}
                      className="rounded border-slate-300"
                    />
                    {PORTAL_PERMISSION_LABELS[p]}
                    {!PORTAL_PERMISSION_BUILT[p] && (
                      <span className="text-xs text-slate-400">(not built yet)</span>
                    )}
                  </label>
                ))}
              </div>
              {permissions.length === 0 && (
                <p className="mt-1.5 text-xs text-slate-500">
                  With nothing ticked they can sign in and see the account name, and no more.
                </p>
              )}
            </fieldset>

            {/* ⚠️ SAYS WHERE THE SCOPE COMES FROM, because it is not obvious and
                it is now the thing that decides. An AM who expects this dialog
                to grant one account will otherwise be surprised when a group
                contact sees seventy. */}
            <p className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">
              They'll see whichever accounts their contact record is linked to
              {grantsDescendants ? ', including everything beneath a group link' : ''} —{' '}
              <span className="font-medium">{account.name}</span> among them. Change that on the
              contact, not here.
            </p>

            {mut.error && (
              <p className="mt-3 text-sm text-rose-600">{(mut.error as Error).message}</p>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={mut.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => mut.mutate()}
                disabled={!chosenEmail || mut.isPending}
              >
                {mut.isPending ? 'Sending…' : 'Send invite'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
